import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useEffect, useMemo } from "react";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import COMMON from "common";
import api from "services/api";
import { formatDatePattern } from "common/calendar";
import { formatCurrency } from "common/format-currency-pattern";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors, { axiosCancelled } from "common/serve-request-errors";
import AppStatus from "components/app-status";
import pdfIcon from "assets/images/pdf-icon.svg";
import AppCloseIcon from "components/icons/app-close-icon";
import rejectIcon from "assets/images/components/pages/leave-management/reject-icon.svg";
import checkedIcon from "assets/images/components/pages/leave-management/checked-icon.svg";
import AppButton from "components/app-button";

export const AppClaimModal = (props, ref) => {
	const [data, setData] = useState({});
	const [visible, setVisible] = useState(false);
	const dispatch = useDispatch();
	const {onHandleGetList, rejectReasonRef, showActions} = props;

	const onHandleShow = useCallback((id) => {
		setData((prev) => ({ ...prev, id }));
		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		setData({});
		setVisible(false);
	}, []);

	const onHandleRejectClaim = useCallback(() => {
		rejectReasonRef?.current.onHandleShow([data.id]);  
		onHandleDismiss();
	}, [data.id, onHandleDismiss, rejectReasonRef]);

	const onHandleApproveClaim = useCallback (async () => {
		try {
			await api.post.claims.approve({ ids : [data.id]});
			dispatch(promptAlertMessage({ message: "Claim has been approved successfully"}));
			onHandleGetList?.();
			onHandleDismiss();
		} catch (error) {
			serveRequestErrors(error);
		}
	}, [data.id, dispatch, onHandleGetList, onHandleDismiss]);

	const productInfo = useMemo(() => {
		// const approvalAmount = data?.status === COMMON.STATUS_ID.APPROVED ? { label: "Approved Amount (RM)", value: data?.approvedClaimValue ? formatCurrency(data.approvedClaimValue) : "-" } : {};
		const rejectReason = data?.status === COMMON.STATUS_ID.REJECTED ? { label: "Reject Reason", value: data?.rejectReason || "-" } : {};

		const info = [
			{ label: "User Name", value: data.name || "-" },
			{ label: "Employee ID", value: data.employeeId || "-" },
			{ label: "Submit Date", value: formatDatePattern(data.createdDate) || "-" },
			{ label: "Visit Date", value: formatDatePattern(data.actualDate) || "-" },
			{ label: "Bill Amount (RM)", value: data.claimValue ? formatCurrency(data.claimValue) : "-" },
			{ label: "Approved Amount (RM)", value: data?.status === COMMON.STATUS_ID.APPROVED && data.approvedClaimValue? formatCurrency(data.approvedClaimValue) : "-" },
			// approvalAmount && { ...approvalAmount },
			{ label: "Attachment", value: data?.companyClaimApplicationFiles?.[0] || "-" },
			{ label: "E-Invoice Exempted", value: data?.eInvoiceExempted ? "Yes" : "No" },
			{ label: "Description", value: data.description || "-" },
			...(rejectReason.label ? [rejectReason] : []),
			({ label: "Remarks", value: data?.remarks || "-" }),
		].filter((o) => o.label);

		return info;
	}, [data]);

	const checkerButtons = useMemo(() => {
		if (data.status !== COMMON.STATUS_ID.PENDING) return [];
		return [
			{ label: "Reject Claim", outline: true, icon: rejectIcon, onClick: onHandleRejectClaim },
			{ label: "Approve Claim", icon: checkedIcon, onClick: onHandleApproveClaim },
		];
	}, [data.status, onHandleRejectClaim, onHandleApproveClaim]);	

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	useEffect(() => {
		const onHandleGetDetails = async () => {
			let response = null;
			try {
				response = await api.get.claims.claim(data.id);
			} catch (error) {
				// the record may be gone when arriving from a notification, so close
				// rather than leave an empty modal behind the error toast
				if (!axiosCancelled(serveRequestErrors(error))) onHandleDismiss();
			}
			if (response) {
				setData((prev) => ({ ...prev, ...response }));
			}
		};

		if (data.id) onHandleGetDetails();
	}, [data.id, onHandleDismiss]);

	return (
		<Modal classes={{ root: "app-claim-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="claim-modal" aria-describedby="claim-modal">
			<div className="claim-modal">
				<div className="claim-modal__main">
					<button className="claim-modal__close-button" onClick={onHandleDismiss}>
						<AppCloseIcon color="#666666" />
					</button>

					<div className="claim-modal__header">
						<div className="claim-modal__profile">
							<p className="claim-modal__title">Claim Details</p>
						</div>
					</div>

					<div className="info">
						<div className="info__box">
							<p className="claim-modal__text">Claim Type</p>
							<p className="claim-modal__title">{data.claimTypeMapped}</p>
						</div>
						<div className="info__box">
							<p className="claim-modal__text">Status</p>
							<div className="claim-modal__title">
								<AppStatus status={data?.status || "-"} />
							</div>
						</div>
					</div>

					<div className="claim-modal__body">
						<div className="claim-modal__container">
							<div className="claim-modal__wrapper">
								{productInfo.map((o, i) => {
									const fileName = o.value?.fileName;

									return (
										<div className="claim-modal__info" key={i}>
											<p className="claim-modal__label">{o.label}</p>
											{!fileName && <p className="claim-modal__value">{o.value}</p>}
											{fileName && (
												<p className="claim-modal__value">
													<a href={o.value.fileURL} className="claim-modal__link" target="_blank" rel="noopener noreferrer">
														<img src={pdfIcon} alt="attachment" /> {fileName}
													</a>
												</p>
											)}
										</div>
									);
								})}
							</div>
							<div className="claim-modal__button-container">
								{showActions && checkerButtons.map ((btn, i) => (
									<AppButton key={i} type="button" outline={btn.outline} icon={btn.icon} label={btn.label}  onClick={btn.onClick} />
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppClaimModal));

AppClaimModal.propTypes = {
	ref: PropTypes.object,
	onHandleGetList: PropTypes.func,
	rejectReasonRef: PropTypes.object,
	showActions: PropTypes.bool,
};
